import React, { useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Checkbox,
  FormControlLabel,
  Alert,
  AlertTitle,
  ButtonGroup,
  Box,
  TextField,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import muiTheme, { DIALOG_RADIUS } from "../../../theme/mui/styles";

/**
 * MUI export modal for vendors. Preserves existing export logic:
 * - select/deselect columns
 * - optional vendor summary
 * - copy (if allowed) or download TSV/Excel when large
 */
const VendorsExportModal = ({
  isOpen,
  onClose,
  columns,
  selectedColumns,
  onChangeColumns,
  includeVendorSummary,
  onToggleVendorSummary,
  showMetricsDateRange = false,
  metricsStartDate = "",
  metricsEndDate = "",
  onChangeMetricsStartDate,
  onChangeMetricsEndDate,
  onCopy,
  copying,
  isLargeExport = false,
  alwaysDownloadCsv = false,
}) => {
  const theme = muiTheme;

  const allColumnKeys = useMemo(
    () => columns.map((column) => column.key),
    [columns]
  );

  const handleToggleColumn = useCallback(
    (key) => {
      if (selectedColumns.includes(key)) {
        onChangeColumns(selectedColumns.filter((column) => column !== key));
      } else {
        onChangeColumns([...selectedColumns, key]);
      }
    },
    [onChangeColumns, selectedColumns]
  );

  const handleSelectAll = useCallback(() => {
    if (selectedColumns.length === columns.length) {
      onChangeColumns([]);
    } else {
      onChangeColumns(allColumnKeys);
    }
  }, [allColumnKeys, columns.length, onChangeColumns, selectedColumns.length]);

  const hasMetricsSelected = Boolean(includeVendorSummary);

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={Boolean(isOpen)}
        onClose={copying ? undefined : onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: DIALOG_RADIUS, mt: 6 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 22 }}>
          Select Vendor Columns to Export
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 1 }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Button
              variant="text"
              size="small"
              sx={{ alignSelf: "flex-start", textTransform: "none", pl: 0 }}
              onClick={handleSelectAll}
            >
              {selectedColumns.length === columns.length
                ? "Deselect All"
                : "Select All"}
            </Button>

            <Stack spacing={1.5}>
              {columns.map((column) => (
                <FormControlLabel
                  key={column.key}
                  control={
                    <Checkbox
                      checked={selectedColumns.includes(column.key)}
                      onChange={() => handleToggleColumn(column.key)}
                    />
                  }
                  label={column.label}
                  sx={{ ".MuiFormControlLabel-label": { fontWeight: 600 } }}
                />
              ))}
            </Stack>

            {showMetricsDateRange && (
              <Stack spacing={1.5}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasMetricsSelected}
                      onChange={(event) =>
                        onToggleVendorSummary &&
                        onToggleVendorSummary(event.target.checked)
                      }
                    />
                  }
                  label="Vendor Metrics"
                  sx={{ ".MuiFormControlLabel-label": { fontWeight: 600 } }}
                />

                {hasMetricsSelected && (
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label="From"
                      type="date"
                      size="small"
                      value={metricsStartDate || ""}
                      onChange={(event) =>
                        onChangeMetricsStartDate &&
                        onChangeMetricsStartDate(event.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="To"
                      type="date"
                      size="small"
                      value={metricsEndDate || ""}
                      onChange={(event) =>
                        onChangeMetricsEndDate &&
                        onChangeMetricsEndDate(event.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                  </Stack>
                )}
              </Stack>
            )}

            {isLargeExport && !alwaysDownloadCsv && (
              <Alert
                severity="info"
                variant="outlined"
                sx={{
                  borderLeft: "4px solid #536DEC",
                  bgcolor: "#E0ECFF",
                }}
              >
                <AlertTitle sx={{ fontWeight: 700, mb: 0.5 }}>
                  Large export
                </AlertTitle>
                <Box sx={{ color: "#1F2937", fontSize: 14 }}>
                  This vendor list is large, so copying to the clipboard isn't available. Download a TSV or Excel file instead.
                </Box>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={onClose}
            disabled={copying}
            sx={{ textTransform: "none", color: "#536DEC", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          {alwaysDownloadCsv ? (
            <Button
              variant="contained"
              onClick={() => onCopy("csv")}
              disabled={copying}
            >
              Download CSV
            </Button>
          ) : isLargeExport ? (
            <ButtonGroup>
              <Button
                variant="outlined"
                onClick={() => onCopy("tsv")}
                disabled={copying}
              >
                Download TSV
              </Button>
              <Button
                variant="contained"
                onClick={() => onCopy("excel")}
                disabled={copying}
              >
                Download Excel
              </Button>
            </ButtonGroup>
          ) : (
            <Button
              variant="contained"
              onClick={() => onCopy("clipboard")}
              disabled={copying}
            >
              Copy Selected Columns
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default VendorsExportModal;
